import { useEffect, useRef, type ReactNode } from "react";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";

// A panel that slides in from the right, over the page: what a page keeps to
// hand without giving it a section of its own (the devices on /shares). Like
// Confirm it is part of the page — role="dialog", its close a real button —
// and Esc or a click outside closes it. Its Esc and Tab are every modal
// dialog's (components/Modal): a question asked from inside it (Confirm,
// rendered outside the drawer) is on top of it, so it answers its own Esc
// without closing the drawer under it. For the same question, "outside" is
// its backdrop alone, not useDismiss's any press outside: pressing the
// question's own buttons must not close the drawer.


export function Drawer({ id, title, open, onClose, className, children }: {
  /** the element id, which the opening button names in aria-controls */
  id: string;
  title: ReactNode;
  open: boolean;
  onClose: () => void;
  /** the drawer's own class beside drawer: one that needs more room (a job's log) */
  className?: string;
  children: ReactNode;
}) {
  const close = useRef<HTMLButtonElement>(null);
  const keys = useModalKeys(onClose, open);

  useEffect(() => {
    if (!open) return;
    // focus moves into the drawer, and back to what opened it on close: the
    // button naming it in aria-controls, since Safari does not focus a button
    // it clicks, so the active element is often only the body
    const opener =
      document.querySelector<HTMLElement>(`[aria-controls="${id}"]`) ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    close.current?.focus();
    return () => opener?.focus();
  }, [open, id]);

  if (!open) return null;
  return (
    <div className="drawer-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <aside
        id={id}
        className={`drawer${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        onKeyDown={keys}
      >
        <header className="drawer-head">
          <h2 id={`${id}-title`}>{title}</h2>
          <button ref={close} className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="drawer-body">{children}</div>
      </aside>
    </div>
  );
}
